import { apiClient } from '@/lib/api/client'
import { ApiError } from '@/lib/api/errors'
import type { ApiErrorCode } from '@/lib/api/errors'
import type { CursorPage } from '@/lib/api/pagination'
import { useAuthStore } from '@/lib/auth/store'
import { env } from '@/lib/env'
import { canonicalDecimal, canonicalTipoCambio } from './lib/canonical-decimal'
import type {
  CancelInvoiceRequest,
  CancelInvoiceResponse,
  CreatePaymentComplementRequest,
  CreatePaymentComplementResponse,
  InvoiceDetail,
  InvoiceDraft,
  InvoiceDraftPatchResult,
  InvoiceDraftSearchResponse,
  InvoiceFilters,
  InvoiceListItem,
  InvoiceStats,
  PatchInvoiceDraftRequest,
  PaymentComplementSummary,
  SatCatalogItem,
  SatMoneda,
  SatMotivoCancelacion,
  StampInvoiceDraftResponse,
  UpsertInvoiceDraftRequest,
} from './types'

function serializeConcepto<
  T extends {
    cantidad: number
    valorUnitario: number
    descuento: number
    impuestos: {
      traslados: Array<{ tasaOCuota: number | null }>
      retenciones: Array<{ tasaOCuota: number | null }>
    }
  },
>(concepto: T) {
  return {
    ...concepto,
    cantidad: canonicalDecimal(concepto.cantidad, 2),
    valorUnitario: canonicalDecimal(concepto.valorUnitario, 6),
    descuento: canonicalDecimal(concepto.descuento, 2),
    impuestos: {
      traslados: concepto.impuestos.traslados.map((item) => ({
        ...item,
        tasaOCuota: item.tasaOCuota === null ? null : canonicalDecimal(item.tasaOCuota, 6),
      })),
      retenciones: concepto.impuestos.retenciones.map((item) => ({
        ...item,
        tasaOCuota: item.tasaOCuota === null ? null : canonicalDecimal(item.tasaOCuota, 6),
      })),
    },
  }
}

function serializeDraftRequest(data: UpsertInvoiceDraftRequest) {
  return {
    ...data,
    cfdi: {
      ...data.cfdi,
      tipoCambio: canonicalTipoCambio(data.cfdi.moneda, data.cfdi.tipoCambio),
      conceptos: data.cfdi.conceptos.map(serializeConcepto),
    },
  }
}

function serializePatchRequest(data: PatchInvoiceDraftRequest) {
  return {
    ...data,
    ops: data.ops.map((op) => {
      if (op.op === 'addConcepto' || op.op === 'updateConcepto') {
        return { ...op, concepto: serializeConcepto(op.concepto) }
      }
      if (op.op === 'replaceConceptos') {
        return { ...op, conceptos: op.conceptos.map(serializeConcepto) }
      }
      if (op.op === 'setPago') {
        return {
          ...op,
          tipoCambio: canonicalTipoCambio(op.moneda, op.tipoCambio),
        }
      }
      return op
    }),
  }
}

function buildSearchQuery(
  filters: InvoiceFilters,
  cursor: string | null,
  pageSize: number,
): string {
  const params = new URLSearchParams()
  if (filters.search.trim() !== '') params.set('search', filters.search.trim())
  for (const s of filters.status) params.append('status', s)
  for (const t of filters.tipos) params.append('tipos', t)
  if (filters.metodoPago) params.set('metodoPago', filters.metodoPago)
  if (filters.clientId) params.set('clientId', filters.clientId)
  if (filters.dateFrom) params.set('dateFrom', filters.dateFrom)
  if (filters.dateTo) params.set('dateTo', filters.dateTo)
  if (cursor !== null) params.set('cursor', cursor)
  params.set('pageSize', String(pageSize))
  return params.toString()
}

async function downloadAuthenticated(
  path: string,
): Promise<{ blob: Blob; fileName: string | null }> {
  const accessToken = useAuthStore.getState().accessToken
  const headers: Record<string, string> = { 'X-Abakoo-Key': env.VITE_ABAKOO_API_KEY }
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`

  const response = await fetch(`${env.VITE_API_URL}${path}`, { headers, credentials: 'include' })

  if (!response.ok) {
    const errorBody = (await response.json().catch(() => ({}))) as Record<string, unknown>
    throw new ApiError(
      response.status,
      (errorBody.errorCode as ApiErrorCode | undefined) ?? 'UNKNOWN',
      (errorBody.detail as string | undefined) ??
        (errorBody.title as string | undefined) ??
        response.statusText,
      errorBody.correlationId as string | undefined,
      errorBody,
    )
  }

  const disposition = response.headers.get('Content-Disposition')
  const match = disposition?.match(/filename="?([^"]+)"?/)
  const fileName = match?.[1] ?? null

  return { blob: await response.blob(), fileName }
}

export const invoicesApi = {
  // ── Catálogos SAT ────────────────────────────────────────────────────────
  listSatMetodosPago: (): Promise<SatCatalogItem[]> =>
    apiClient('/api/v1/invoices/sat/metodos-pago'),

  listSatMotivosCancelacion: (): Promise<SatMotivoCancelacion[]> =>
    apiClient('/api/v1/invoices/sat/motivos-cancelacion'),

  listSatExportaciones: (): Promise<SatCatalogItem[]> =>
    apiClient('/api/v1/invoices/sat/exportaciones'),

  listSatTiposRelacion: (): Promise<SatCatalogItem[]> =>
    apiClient('/api/v1/invoices/sat/tipos-relacion'),

  listSatMonedas: (): Promise<SatMoneda[]> => apiClient('/api/v1/invoices/sat/monedas'),

  listSatPeriodicidades: (): Promise<SatCatalogItem[]> =>
    apiClient('/api/v1/invoices/sat/periodicidades'),

  listSatMeses: (): Promise<SatCatalogItem[]> => apiClient('/api/v1/invoices/sat/meses'),

  // ── Borradores ───────────────────────────────────────────────────────────
  searchDrafts: (page: number, pageSize: number): Promise<InvoiceDraftSearchResponse> =>
    apiClient(`/api/v1/invoices/draft?page=${page}&pageSize=${pageSize}`),

  getDraft: (id: string): Promise<InvoiceDraft> => apiClient(`/api/v1/invoices/draft/${id}`),

  createDraft: (data: UpsertInvoiceDraftRequest): Promise<InvoiceDraft> =>
    apiClient('/api/v1/invoices/draft', { method: 'POST', body: serializeDraftRequest(data) }),

  patchDraft: (
    id: string,
    data: PatchInvoiceDraftRequest,
    correlationId: string,
    idempotencyKey: string,
  ): Promise<InvoiceDraftPatchResult> =>
    apiClient(`/api/v1/invoices/draft/${id}`, {
      method: 'PATCH',
      body: serializePatchRequest(data),
      headers: {
        'X-Correlation-ID': correlationId,
        'Idempotency-Key': idempotencyKey,
      },
    }),

  deleteDraft: (id: string): Promise<void> =>
    apiClient(`/api/v1/invoices/draft/${id}`, { method: 'DELETE' }),

  stampDraft: (
    id: string,
    idempotencyKey: string,
    correlationId?: string,
  ): Promise<StampInvoiceDraftResponse> =>
    apiClient(`/api/v1/invoices/draft/${id}/stamp`, {
      method: 'POST',
      headers: {
        'Idempotency-Key': idempotencyKey,
        ...(correlationId ? { 'X-Correlation-ID': correlationId } : {}),
      },
    }),

  // ── Facturas timbradas ───────────────────────────────────────────────────
  search: (
    filters: InvoiceFilters,
    cursor: string | null,
    pageSize: number,
  ): Promise<CursorPage<InvoiceListItem>> =>
    apiClient(`/api/v1/invoices?${buildSearchQuery(filters, cursor, pageSize)}`),

  getById: (id: string): Promise<InvoiceDetail> => apiClient(`/api/v1/invoices/${id}`),

  getStats: (): Promise<InvoiceStats> => apiClient('/api/v1/invoices/stats'),

  downloadXml: (id: string): Promise<{ blob: Blob; fileName: string | null }> =>
    downloadAuthenticated(`/api/v1/invoices/${id}/xml`),

  downloadPdf: (id: string): Promise<{ blob: Blob; fileName: string | null }> =>
    downloadAuthenticated(`/api/v1/invoices/${id}/pdf`),

  /** Acuse de cancelación (XML firmado por el SAT). 422 ACUSE_NOT_AVAILABLE si no se pidió cancelar. */
  downloadAcuse: (id: string): Promise<{ blob: Blob; fileName: string | null }> =>
    downloadAuthenticated(`/api/v1/invoices/${id}/acuse`),

  cancel: (
    id: string,
    data: CancelInvoiceRequest,
    idempotencyKey: string,
  ): Promise<CancelInvoiceResponse> =>
    apiClient(`/api/v1/invoices/${id}/cancel`, {
      method: 'POST',
      body: data,
      headers: { 'Idempotency-Key': idempotencyKey },
    }),

  listPaymentComplements: (id: string): Promise<PaymentComplementSummary[]> =>
    apiClient(`/api/v1/invoices/${id}/payment-complements`),

  createPaymentComplement: (
    id: string,
    data: CreatePaymentComplementRequest,
    idempotencyKey: string,
  ): Promise<CreatePaymentComplementResponse> =>
    apiClient(`/api/v1/invoices/${id}/payment-complement`, {
      method: 'POST',
      body: data,
      headers: { 'Idempotency-Key': idempotencyKey },
    }),
}
